import React, { useEffect, useState } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import { useSnackbar } from "notistack";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import { Box, Button } from "@mui/material";
import { useParams } from "react-router-dom";
import useBackend from "../../hooks/use_backend";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { fetchDeviceMetering } from "../../scripts/api_v2/metering_client";
import { fetchSiteData } from "../../scripts/api_v2/site_client";
import { useQuery } from "@tanstack/react-query";
import Loader from "../general/loader";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { LIVE_DATA_WARNINGS } from "../../pages/elion_site/tabs/commissioning_tab/components/enum/live_data_warnings";
import { COMMISSIONING_WIZARD_REDUCER } from "../../pages/elion_site/tabs/commissioning_tab/components/hooks/commissioning_wizard_reducer";
import { useTheme } from "@emotion/react";

ChartJS.register(
  TimeScale,
  LinearScale,
  CategoryScale,
  LineController,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

export const getDeviceChartOptions = () => ({
  animation: false,
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  plugins: {
    legend: {
      position: "bottom",
      display: true,
    },
  },
  scales: {
    y: {},
  },
});

export function DeviceMetering({ device, dispatchCommissioningStateChanges = null, timeDelta = null }) {
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const backend = useBackend();

  const [draftDate, setDraftDate] = useState(dayjs());
  const [draftStartTime, setDraftStartTime] = useState(null);
  const [draftEndTime, setDraftEndTime] = useState(null);
  const [filterStart, setFilterStart] = useState(() =>
    timeDelta != null ? draftDate.subtract(timeDelta, "hours") : draftDate.startOf("day"),
  );
  const [filterEnd, setFilterEnd] = useState(() => draftDate.endOf("day"));

  const fromDate = timeDelta != null ? draftDate.subtract(timeDelta, "hours") : draftDate.startOf("day");

  const toDate = draftDate.endOf("day");

  const fromts = fromDate.unix();
  const tots = toDate.unix();

  const {
    data: chartData,
    isFetching,
    error: dataFetchingError,
    fetchStatus,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.DEVICE_METERING, device.device_id, backend.fetch, params.site_id, fromts, tots],
    queryFn: async () => {
      try {
        return await fetchDeviceMetering(
          backend.fetch,
          params.site_id,
          device.device_id,
          device.device_type,
          fromts,
          tots,
        );
      } catch (error) {
        console.error("Error in query function:", error);
        throw error; // Rethrow to let react-query handle it
      }
    },
    refetchInterval: 5 * 60 * 1000,
    enabled: !!params.site_id,
  });

  useEffect(() => {
    if (dispatchCommissioningStateChanges != null && !isFetching && chartData != null) {
      dispatchCommissioningStateChanges({
        type: COMMISSIONING_WIZARD_REDUCER.METERING_DATA_FETCHED,
        payload: true,
      });
    } else if (dispatchCommissioningStateChanges != null && chartData === null) {
      dispatchCommissioningStateChanges({
        type: COMMISSIONING_WIZARD_REDUCER.METERING_DATA_FETCHED,
        payload: false,
      });
    }
  }, [isFetching && chartData]);

  useEffect(() => {
    if (
      dispatchCommissioningStateChanges != null &&
      ((!isFetching && (chartData == null || chartData.length === 0)) || dataFetchingError)
    ) {
      dispatchCommissioningStateChanges({
        type: COMMISSIONING_WIZARD_REDUCER.STEP_WARNING_FOUND,
        payload: LIVE_DATA_WARNINGS.DEVICE_METERING_COMPONENT,
      });
    }
  }, [dataFetchingError, chartData, isFetching]);

  let {
    data: siteData,
    siteError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  if (isFetching)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" height="100%" width="100%">
        <Loader fill={true} />
      </Box>
    );

  if (dataFetchingError) return <div>Error loading data</div>;

  if (chartData.length === 0) return <div>No data availabe</div>;

  const filteredChartData = chartData.filter(
    (el) => el.utctime >= filterStart.unix() && el.utctime <= filterEnd.unix(),
  );

  const labels = filteredChartData.map((el) => new Date(1000 * el.utctime));
  const data = {
    flex: filteredChartData.map((el) => Math.round(el.flex)),
    setpoint: filteredChartData.map((el) => Math.round(el.setpoint)),
    l1: filteredChartData.map((el) => Math.round(el.l1)),
    l2: filteredChartData.map((el) => Math.round(el.l2)),
    l3: filteredChartData.map((el) => Math.round(el.l3)),
    production: filteredChartData.map((el) => Math.round(el.production)),
    production_setpoint: filteredChartData.map((el) => Math.round(el.production_setpoint)),
    curtailment: filteredChartData.map((el) => Math.round(el.curtailment)),
    reactive_l1: filteredChartData.map((el) => Math.round(el.reactive_l1)),
    reactive_l2: filteredChartData.map((el) => Math.round(el.reactive_l2)),
    reactive_l3: filteredChartData.map((el) => Math.round(el.reactive_l3)),
    grid_l1: filteredChartData.map((el) => Math.round(el.grid_l1)),
    grid_l2: filteredChartData.map((el) => Math.round(el.grid_l2)),
    grid_l3: filteredChartData.map((el) => Math.round(el.grid_l3)),
  };

  return (
    <Box display="flex" width="100%" height="80%" flexDirection="column" overflowY="auto" mt={10}>
      <Box display="flex" justifyContent="center" alignItems="center" gap={2} mb={2}>
        <DatePicker
          label="Date"
          value={draftDate}
          format="DD-MM-YYYY"
          onChange={(newDate) => {
            if (!newDate || !newDate.isValid()) return;

            setDraftDate(newDate);
            setFilterStart(newDate.startOf("day"));
            setFilterEnd(newDate.endOf("day"));
            setDraftStartTime(null);
            setDraftEndTime(null);
          }}
        />
        <TimePicker label="Start Time (optional)" value={draftStartTime} onChange={setDraftStartTime} ampm={false} />
        <TimePicker label="End Time (optional)" value={draftEndTime} onChange={setDraftEndTime} ampm={false} />
        <Button
          variant="contained"
          onClick={() => {
            if (!draftDate || !draftDate.isValid()) {
              return enqueueSnackbar("Please select a valid date.", { variant: "error" });
            }

            let from = draftDate
              .hour(draftStartTime?.hour() ?? 0)
              .minute(draftStartTime?.minute() ?? 0)
              .second(0);
            let to = draftDate
              .hour(draftEndTime?.hour() ?? 23)
              .minute(draftEndTime?.minute() ?? 59)
              .second(59);

            if (to.isBefore(from)) {
              return enqueueSnackbar("End time cannot be before start time.", { variant: "error" });
            }

            const diffInHours = to.diff(from, "hour", true);
            if (diffInHours > 24) {
              return enqueueSnackbar("The selected interval can not exceed 24 hours.", { variant: "error" });
            }

            setFilterStart(from);
            setFilterEnd(to);
          }}
        >
          Apply
        </Button>
      </Box>

      <Box height="80%" width="100%" display="flex" flexDirection="column">
        {device.device_class === "battery" && <DeviceFlexChart labels={labels} data={data} />}
        {(device.device_class === "meter" || siteData?.devices_general?.schematic?.device_id === device.device_id) && (
          <DeviceGridChart labels={labels} data={data} />
        )}
        {!data.reactive_l1.every((value) => Number.isNaN(value)) && <DeviceReactiveChart labels={labels} data={data} />}
        {!data.production.every((value) => Number.isNaN(value)) && (
          <DeviceCurtailmentChart labels={labels} data={data} />
        )}
      </Box>
    </Box>
  );
}
const DeviceMeteringChart = ({ data, options, sx }) => {
  return (
    <Box boxSizing="border-box" display="flex" height="250px" width="100%">
      <Line data={data} options={options} width="100%" />
    </Box>
  );
};

const DeviceFlexChart = ({ labels, data, device }) => {
  const options = getDeviceChartOptions();
  options.scales.y.suggestedMin = -device?.charge_rate * 1.1;
  options.scales.y.suggestedMax = device?.charge_rate * 1.1;

  options.scales.x = {
    type: "time",
    time: {
      unit: "minute",
      displayFormats: {
        minute: "HH:mm",
      },
    },
  };

  const flexData = {
    labels: labels,
    datasets: [
      {
        fill: false,
        label: "Flex",
        data: data.flex,
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(255, 153, 0, 1)",
        backgroundColor: "rgba(255, 153, 0, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "Flex setpoint",
        data: data.setpoint,
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(100, 100, 100, 0.5)",
        backgroundColor: "rgba(100, 100, 100, 0.5)",
        pointStyle: false,
        type: "line",
      },
    ],
  };
  return <DeviceMeteringChart sx={{ pt: 8 }} data={flexData} options={options} />;
};

const DeviceGridChart = ({ labels, data }) => {
  const params = useParams();
  const backend = useBackend();

  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });
  const options = getDeviceChartOptions();
  // options.scales.y.suggestedMin = -siteData?.config?.max_grid_inject_sp * 1.1;
  // options.scales.y.suggestedMax = siteData?.config?.max_grid_offtake_sp * 1.1;

  options.scales.x = {
    type: "time",
    time: {
      unit: "minute",
      displayFormats: {
        minute: "HH:mm",
      },
    },
  };

  const gridData = {
    labels: labels,
    datasets: [
      {
        fill: false,
        label: "L1 Active",
        data: data.grid_l1.every((v) => isNaN(v)) ? data.l1 : data.grid_l1,
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(102, 0, 205, 1)",
        backgroundColor: "rgba(102, 0, 205, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "L2 Active",
        data: data.grid_l2.every((v) => isNaN(v)) ? data.l2 : data.grid_l2,
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(0, 102, 205, 1)",
        backgroundColor: "rgba(0, 102, 205, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "L3 Active",
        data: data.grid_l3.every((v) => isNaN(v)) ? data.l3 : data.grid_l3,
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(205, 102, 0, 1)",
        backgroundColor: "rgba(205, 102, 0, 1)",
        pointStyle: false,
        type: "line",
      },
    ],
  };
  return <DeviceMeteringChart data={gridData} options={options} />;
};

const DeviceCurtailmentChart = ({ labels, data }) => {
  const params = useParams();
  const backend = useBackend();
  const theme = useTheme();

  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  const options = getDeviceChartOptions();
  options.scales.y.suggestedMin = 0;
  options.scales.y.suggestedMax = siteData?.config?.max_grid_offtake_sp * 1.1;

  options.scales.x = {
    type: "time",
    time: {
      unit: "minute",
      displayFormats: {
        minute: "HH:mm",
      },
    },
  };

  const gridData = {
    labels: labels,
    datasets: [
      {
        fill: false,
        label: "Total production",
        data: data.production,
        borderWidth: 2,
        //backgroundColor: 'transparent',
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(0, 204, 0, 1)",
        backgroundColor: "rgba(0, 204, 0, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "Production with curtailment",
        data: data.production.map((p, i) => p - data.curtailment[i]),
        borderWidth: 2,
        //backgroundColor: 'transparent',
        cubicInterpolationMode: "monotone",
        borderColor: theme.palette.curtailment.main,
        backgroundColor: theme.palette.curtailment.main,
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "Production setpoint",
        data: data.production_setpoint,
        borderWidth: 2,
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(100, 100, 100, 0.5)",
        backgroundColor: "rgba(100, 100, 100, 0.5)",
        pointStyle: false,
        type: "line",
      },
    ],
  };
  return <DeviceMeteringChart data={gridData} options={options} />;
};

const DeviceReactiveChart = ({ labels, data }) => {
  const options = getDeviceChartOptions();

  options.scales.x = {
    type: "time",
    time: {
      unit: "minute",
      displayFormats: {
        minute: "HH:mm",
      },
    },
  };

  const gridData = {
    labels: labels,
    datasets: [
      {
        fill: false,
        label: "L1 Reactive",
        data: data.reactive_l1,
        borderWidth: 2,
        //backgroundColor: 'transparent',
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(102, 0, 205, 1)",
        backgroundColor: "rgba(102, 0, 205, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "L2 Reactive",
        data: data.reactive_l2,
        borderWidth: 2,
        //backgroundColor: 'transparent',
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(0, 102, 205, 1)",
        backgroundColor: "rgba(0, 102, 205, 1)",
        pointStyle: false,
        type: "line",
      },
      {
        fill: false,
        label: "L3 Reactive",
        data: data.reactive_l3,
        borderWidth: 2,
        //backgroundColor: 'transparent',
        cubicInterpolationMode: "monotone",
        borderColor: "rgba(205, 102, 0, 1)",
        backgroundColor: "rgba(205, 102, 0, 1)",
        pointStyle: false,
        type: "line",
      },
    ],
  };
  return <DeviceMeteringChart data={gridData} options={options} />;
};
